<script setup>
import { computed, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useAuthStore } from '../../stores/auth';
import { useProfilesStore } from '../../stores/profiles';
import { Users, Heart, Sparkles, X, ArrowRight } from 'lucide-vue-next';
import IconDisc from '../brand/IconDisc.vue';

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const profilesStore = useProfilesStore();
const { user } = storeToRefs(authStore);
const { profiles, defaultProfile } = storeToRefs(profilesStore);

// Affichée au 1er login post-déploiement pour les comptes legacy migrés. Si le user
// la ferme via la croix, on bascule sur la bannière de rappel (modalDismissed: true).
// Si le user agit (CTA), on considère la découverte faite (profilesDiscovered: true).
// Compte migré (vraies prefs legacy reprises) vs nouveau compte : copy différente.
const isMigrated = computed(() => {
  const def = defaultProfile.value;
  return !!(def && typeof def._migratedFrom === 'string' && def._migratedFrom.length > 0);
});

const visible = computed(() => {
  if (!user.value) return false;
  if (user.value.onboarding?.modalDismissed === true) return false;
  // Le funnel cinematique (/bienvenue) vient de tout collecter (profils, gouts) :
  // la modale de decouverte serait redondante juste apres.
  if (user.value.onboardingCompletedAt) return false;
  if (!profiles.value || profiles.value.length === 0) return false;
  const def = defaultProfile.value;
  if (!def) return false;
  // Affichée aux comptes migrés (legacy) ET aux nouveaux comptes (onboarding foyer).
  return isMigrated.value || def._origin === 'new_signup';
});

const busy = ref(false);

async function _writeOnboarding(patch) {
  if (busy.value) return;
  busy.value = true;
  try {
    await authStore.updateProfile({ onboarding: { ...(user.value?.onboarding || {}), ...patch } });
  } catch (e) {
    console.warn('[profilesOnboarding] write failed (silent):', e?.message);
  } finally {
    busy.value = false;
  }
}

function dismissSoft() {
  // Croix ou "Plus tard" : on bascule sur la bannière nudge (qui reste tant que
  // l'user n'a pas concrètement adopté la nouveauté).
  _writeOnboarding({ modalDismissed: true });
}
async function startQuiz() {
  const def = defaultProfile.value;
  if (!def?.id) return;
  await _writeOnboarding({ modalDismissed: true });
  // En parcours de bienvenue, on transmet onboarding=1 pour finir sur la commande.
  const query = String(route.query.onboarding) === '1' ? { onboarding: 1 } : {};
  router.push({ path: `/compte/profils/${def.id}/gouts`, query });
}
</script>

<template>
  <Teleport to="body">
    <div v-if="visible" class="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div class="absolute inset-0 bg-black/60" @click="dismissSoft"></div>
      <div class="relative w-full md:max-w-md bg-white rounded-[28px] shadow-2xl overflow-hidden animate-slide-up">
        <button type="button" @click="dismissSoft" :disabled="busy"
          class="absolute top-3 right-3 text-gray-400 hover:text-gray-700 transition-colors p-1.5 rounded-full hover:bg-gray-100 z-10 disabled:opacity-30"
          aria-label="Fermer">
          <X class="w-4 h-4" />
        </button>

        <!-- Hero illustration : 3 profils foyer aux couleurs DA -->
        <div class="bg-[#fffdf8] px-6 pt-8 pb-5">
          <div class="flex items-center justify-center gap-3 mb-3">
            <div class="w-12 h-12 rounded-full bg-[#ffd93e] text-white font-extrabold text-lg flex items-center justify-center shadow-md ring-4 ring-white -rotate-6">
              P
            </div>
            <div class="w-14 h-14 rounded-full bg-[#e94591] text-white font-extrabold text-xl flex items-center justify-center shadow-md ring-4 ring-white">
              M
            </div>
            <div class="w-12 h-12 rounded-full bg-[#0077B6] text-white font-extrabold text-lg flex items-center justify-center shadow-md ring-4 ring-white rotate-6">
              E
            </div>
          </div>
          <div class="text-center">
            <span class="inline-block bg-white/80 text-[#111] text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full">
              {{ isMigrated ? 'Nouveauté' : 'Bienvenue' }}
            </span>
            <h2 class="text-xl md:text-2xl font-extrabold text-gray-900 mt-2 leading-tight">
              {{ isMigrated ? 'Toute la famille a son profil' : 'Crée les profils de ton foyer' }}
            </h2>
            <p class="text-sm text-gray-600 mt-1">
              {{ isMigrated ? 'Tes préférences ont été reprises sur ton profil principal.' : 'Un profil par personne : régime, allergies et goûts.' }}
            </p>
          </div>
        </div>

        <!-- 3 mini-features -->
        <div class="px-5 py-4 space-y-2.5">
          <div class="flex items-start gap-3">
            <IconDisc color="yellow" size="sm" shape="square"><Users /></IconDisc>
            <div class="min-w-0 flex-1">
              <p class="text-sm font-bold text-gray-900 leading-tight">Un profil par membre du foyer</p>
              <p class="text-xs text-gray-500 leading-snug">Chacun a son régime, ses allergies, ses goûts.</p>
            </div>
          </div>
          <div class="flex items-start gap-3">
            <IconDisc color="pink" size="sm" shape="square" :delay="90"><Heart /></IconDisc>
            <div class="min-w-0 flex-1">
              <p class="text-sm font-bold text-gray-900 leading-tight">Tes goûts, en quelques swipes</p>
              <p class="text-xs text-gray-500 leading-snug">J'aime, j'aime pas. Plus tu votes, mieux on cible.</p>
            </div>
          </div>
          <div class="flex items-start gap-3">
            <IconDisc color="blue" size="sm" shape="square" :delay="180"><Sparkles /></IconDisc>
            <div class="min-w-0 flex-1">
              <p class="text-sm font-bold text-gray-900 leading-tight">Des plats qui te ressemblent</p>
              <p class="text-xs text-gray-500 leading-snug">Chaque commande est adaptée à chaque profil.</p>
            </div>
          </div>
        </div>

        <!-- CTAs -->
        <div class="px-5 pb-5 pt-2 flex flex-col gap-2">
          <button type="button" @click="startQuiz" :disabled="busy"
            class="w-full inline-flex items-center justify-center gap-2 bg-[#ffd93e] hover:bg-[#ffcf0f] text-[#111] text-sm font-bold px-4 py-3 rounded-xl transition-colors disabled:opacity-50">
            Commencer maintenant
            <ArrowRight class="w-4 h-4" />
          </button>
          <button type="button" @click="dismissSoft" :disabled="busy"
            class="w-full inline-flex items-center justify-center text-gray-500 hover:text-gray-900 text-sm font-bold px-4 py-2 rounded-xl transition-colors disabled:opacity-50">
            Plus tard
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.animate-slide-up {
  animation: slide-up 0.35s cubic-bezier(0.32, 0.72, 0, 1);
}
@keyframes slide-up {
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}
</style>
